import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ChevronRightIcon, InboxIcon } from '../components/ui/Icons';
import {
  NotificationItem,
  getNotifications,
  markNotificationsRead,
  notificationLink,
} from '../notifications/api';
import { eventVisual, modeVisual, statusColor } from '../notifications/visuals';
import sidebarStyles from './Sidebar.module.css';
import styles from './NotificationBell.module.css';
import { besideOrAbove, PopoverPosition } from './popoverPosition';

/**
 * BANDEJA DE NOTIFICACIONES en la barra lateral.
 *
 * ## Por qué en el sidebar y no en el header
 *
 * 🔑 El header **desaparece** en varias pantallas: `GuideDetail` y `UserDetail` llaman a `setHideHeader(true)`
 * y otras lo reemplazan con `headerContent`. Un aviso al que solo se puede llegar desde algunas pantallas no
 * es una bandeja — y peor, el globo con el conteo se apagaría al navegar, que se lee como que los avisos se
 * fueron. El pie del sidebar está en todas las pantallas del área autenticada.
 *
 * ## ⚠️ Lo más importante de este componente: falla en silencio
 *
 * Se dibuja en **cada** pantalla, así que un error suyo no puede ensuciar la aplicación. Si el endpoint
 * responde 500 —por ejemplo porque `SQL_NOTIFICATIONS.sql` no ha corrido— no hay globo, no hay mensaje de
 * error y no hay nada en la consola de nadie: simplemente no hay avisos. Cualquier otra opción convierte una
 * tabla que falta en un error visible en las veinte pantallas de la plataforma.
 */

const REFRESH_MS = 60_000;

const when = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const diff = Date.now() - d.getTime();
  const mins = Math.floor(diff / 60000);
  /**
   * 📌 Tiempo relativo y no la fecha: en una bandeja lo que importa es "¿esto es de ahora o de ayer?", y
   * "hace 4 min" contesta eso de un barrido. La fecha completa queda en el `title`, para quien la necesite.
   */
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};

/**
 * ⚠️ El cálculo de "a dónde lleva un aviso" **se mudó a `notifications/api.ts`**.
 *
 * Vivía aquí como copia local, y cuando nació la página de la bandeja pasó a tener dos dueños. Dos copias de
 * esa regla se separan sin que nada falle: el síntoma sería un aviso que abre la pantalla equivocada desde un
 * lado y la correcta desde el otro, y eso no se reporta como error de código.
 */

interface NotificationBellProps {
  collapsed: boolean;
}

const NotificationBell: React.FC<NotificationBellProps> = ({ collapsed }) => {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [items, setItems] = React.useState<NotificationItem[]>([]);
  const [unread, setUnread] = React.useState(0);
  /**
   * `true` cuando el endpoint no está disponible. **No se muestra**: solo apaga el globo y el panel.
   * Existe para no reintentar en bucle contra algo que no está.
   */
  const [unavailable, setUnavailable] = React.useState(false);
  const [pos, setPos] = React.useState<PopoverPosition>({ left: 0, bottom: 0 });
  const btnRef = React.useRef<HTMLButtonElement>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);

  /**
   * Cuando el conteo SUBE, el globo late una vez.
   *
   * 🔑 Es la única señal de que llegó algo mientras la persona estaba en otra pantalla. El sondeo es cada
   * minuto, así que sin el latido el número cambia en silencio y nadie lo nota hasta que mira a propósito.
   *
   * ⚠️ Solo al **subir**. Si latiera con cualquier cambio, marcar como leído —que baja el conteo— dispararía
   * una animación de "llegó algo nuevo" justo cuando pasó lo contrario.
   */
  const [pulse, setPulse] = React.useState(false);
  const lastUnread = React.useRef(0);

  React.useEffect(() => {
    if (unread > lastUnread.current && lastUnread.current !== 0) {
      setPulse(true);
      const id = window.setTimeout(() => setPulse(false), 1400);
      return () => window.clearTimeout(id);
    }
    lastUnread.current = unread;
    return undefined;
  }, [unread]);

  React.useEffect(() => {
    lastUnread.current = unread;
  }, [unread]);

  const load = React.useCallback(async () => {
    try {
      const data = await getNotifications({ limit: 20 });
      setItems(data.items);
      setUnread(data.unread);
      setUnavailable(false);
    } catch {
      /**
       * ⚠️ Silencio deliberado. Ver la nota del encabezado: este componente vive en todas las pantallas y un
       * aviso de error aquí sería un aviso de error en toda la plataforma.
       */
      setUnavailable(true);
      setItems([]);
      setUnread(0);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  /**
   * Sondeo cada minuto.
   *
   * 📌 Un minuto y no cinco segundos: los avisos los produce el JOB de rastreo, que en producción corre por
   * horas. Sondear más seguido serían peticiones para no encontrar nada. Y no se usa WebSocket porque
   * mantener una conexión abierta por usuario para un aviso cada varias horas es infraestructura nueva para
   * un problema que no existe.
   *
   * ⚠️ **Se detiene si el endpoint no está disponible.** Sin esto, una tabla que falta produciría un 500 por
   * minuto por usuario, indefinidamente.
   */
  React.useEffect(() => {
    if (unavailable) return;
    const id = window.setInterval(() => {
      void load();
    }, REFRESH_MS);
    return () => window.clearInterval(id);
  }, [load, unavailable]);

  const computePos = React.useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    /** 🔄 21-oct-2026: a la derecha del botón si cabe; si no (el cajón del teléfono), encima. Ver `popoverPosition.ts`. */
    setPos(besideOrAbove(r, panelRef.current?.offsetWidth || 330));
  }, []);

  React.useLayoutEffect(() => {
    if (open) computePos();
  }, [open, computePos]);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    const onScrollResize = () => computePos();
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onScrollResize);
    window.addEventListener('scroll', onScrollResize, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onScrollResize);
      window.removeEventListener('scroll', onScrollResize, true);
    };
  }, [open, computePos]);

  const openPanel = () => {
    setOpen((o) => !o);
    // Al abrir se refresca: el sondeo puede tener hasta un minuto de retraso.
    if (!open) void load();
  };

  const markAll = async () => {
    try {
      const { unread: left } = await markNotificationsRead();
      setUnread(left);
      setItems((list) => list.map((n) => ({ ...n, read: true })));
    } catch {
      /* mismo criterio: no se ensucia la interfaz por la bandeja */
    }
  };

  const openItem = async (n: NotificationItem) => {
    setOpen(false);
    /**
     * 📌 Se marca leído **antes** de navegar y sin esperar la respuesta. Si se esperara, el clic tendría un
     * retraso perceptible contra Oregón (~90 ms de ida y vuelta) para un efecto que al usuario le da igual.
     */
    if (!n.read) {
      void markNotificationsRead([n.id]).then(({ unread: left }) => setUnread(left));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    const to = notificationLink(n);
    if (to) navigate(to);
  };

  /** Lleva a la bandeja completa. Cierra el panel primero: si no, quedaría abierto sobre la página nueva. */
  const openInbox = () => {
    setOpen(false);
    navigate('/notifications');
  };

  /**
   * ⚠️ Si el endpoint no está disponible, el botón **no se dibuja**. Un icono de bandeja que al pulsarlo no
   * hace nada es peor que no tenerlo: la gente lo reporta.
   */
  if (unavailable) return null;

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={[sidebarStyles.item, styles.trigger, open ? styles.triggerOpen : '']
          .filter(Boolean)
          .join(' ')}
        onClick={openPanel}
        title={collapsed ? `Notifications${unread ? ` (${unread})` : ''}` : undefined}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className={sidebarStyles.icon}>
          <InboxIcon size={20} />
          {/**
           * El globo va sobre el icono y **no junto a la etiqueta**, porque la etiqueta desaparece con el
           * sidebar colapsado — que es el estado por omisión. Un conteo que solo se ve expandido no sirve.
           */}
          {unread > 0 && (
            <span
              className={[styles.badge, pulse ? styles.badgePulse : ''].filter(Boolean).join(' ')}
              aria-hidden="true"
            >
              {unread > 9 ? '9+' : unread}
            </span>
          )}
        </span>
        <span className={sidebarStyles.label}>Notifications</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            className={styles.panel}
            role="menu"
            style={{
              left: pos.left,
              bottom: pos.bottom,
              /* Encima del botón (el teléfono), no más alto que lo que hay hasta el borde de arriba. */
              maxHeight: pos.maxHeight !== undefined ? `min(60vh, ${pos.maxHeight}px)` : undefined,
            }}
          >
            <div className={styles.head}>
              <span className={styles.headTitle}>Notifications</span>
              {unread > 0 && (
                <button type="button" className={styles.markAll} onClick={markAll}>
                  Mark all read
                </button>
              )}
            </div>

            <div className={styles.list}>
              {/**
               * ## ⚠️ Este texto decía algo que mandaba a dar vueltas
               *
               * Antes: *"Nothing yet. Pick what you want to hear about in My profile."* Le pedía al usuario
               * configurar algo **que ya había configurado** —comprobado contra la base: preferencias
               * guardadas y cero avisos—, así que la conclusión razonable era que la pantalla de perfil
               * estaba rota.
               *
               * 🔑 Ahora dice **qué tiene que pasar**, que es la información que faltaba: los avisos no los
               * produce entrar a la plataforma, los produce que un embarque se mueva. Y manda a la bandeja,
               * donde eso se explica completo y hay un aviso de prueba para descartar una falla.
               */}
              {items.length === 0 && (
                <p className={styles.empty}>
                  Nothing yet. Notifications show up when a shipment moves or runs late.
                </p>
              )}

              {/**
               * 🔑 **El mismo lenguaje visual que la página de la bandeja**, y sale del mismo archivo
               * (`notifications/visuals.tsx`). Si el panel y la página usaran cada uno su propio criterio,
               * "retraso" sería rojo en un lado y naranja en el otro — nadie lo reporta como error y sin
               * embargo destruye lo único que un color aporta, que es reconocer el tipo sin leer.
               */}
              {items.map((n, i) => {
                const event = eventVisual(n.eventType);
                const mode = modeVisual(n.shipmentType);

                return (
                <button
                  key={n.id}
                  type="button"
                  role="menuitem"
                  className={[styles.item, n.read ? '' : styles.itemUnread].filter(Boolean).join(' ')}
                  onClick={() => void openItem(n)}
                  title={new Date(n.createdAt).toLocaleString()}
                  /** Entrada escalonada, con techo de 8: más allá la cascada se siente como lentitud. */
                  style={{ ['--item-delay' as string]: `${Math.min(i, 8) * 26}ms` }}
                >
                  <span className={styles.itemTop}>
                    <span
                      className={[styles.itemIcon, styles[`tone_${event.tone}`]].join(' ')}
                      aria-hidden="true"
                    >
                      {event.icon}
                    </span>
                    <span className={styles.itemTitle}>
                      {n.reference ? <strong>{n.reference}</strong> : null}
                      {n.reference ? ' · ' : null}
                      {n.title}
                    </span>
                    <span className={styles.itemWhen}>{when(n.createdAt)}</span>
                  </span>

                  {(n.status || mode) && (
                    <span className={styles.itemChips}>
                      {n.status && (
                        <span
                          className={styles.chip}
                          /** El color de la tabla de cargas para ese estatus. Ver `statusMeaning`. */
                          style={
                            {
                              '--status-color': statusColor(n.status, n.shipmentType),
                            } as React.CSSProperties
                          }
                        >
                          {n.status}
                        </span>
                      )}
                      {mode && (
                        <span className={styles.mode} title={mode.label}>
                          {mode.icon}
                          {mode.tag}
                        </span>
                      )}
                    </span>
                  )}

                  {n.body && <span className={styles.itemBody}>{n.body}</span>}
                  {/**
                   * 🔑 Se marca cuando el aviso llegó por una **suscripción explícita**, no por el acceso
                   * normal. No es adorno: la suscripción es acceso que la persona se dio a sí misma, y saber
                   * de dónde salió el aviso es lo que hace auditable esa decisión.
                   */}
                  {n.source === 'subscription' && (
                    <span className={styles.itemTag}>following this shipment</span>
                  )}
                </button>
                );
              })}
            </div>

            {/**
             * 🔑 **La salida a la bandeja completa, y va SIEMPRE**, también cuando el panel está vacío.
             *
             * Este panel trae 20 avisos, no filtra y no pagina: es un vistazo, no un lugar. Sin esta salida,
             * el aviso número 21 es inalcanzable desde la interfaz — y peor, con el panel vacío no habría
             * ninguna pista de dónde se explica por qué está vacío.
             */}
            <button type="button" className={styles.footer} role="menuitem" onClick={openInbox}>
              <span>See all notifications</span>
              <ChevronRightIcon size={15} />
            </button>
          </div>,
          document.body
        )}
    </>
  );
};

export default NotificationBell;
